import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, EmptyState, Page } from '@appwin/ui';
import { api, type DeveloperApp, type ReleaseView } from './api.js';
import { AddReleaseDialog } from './AddReleaseDialog.js';

/** One app: its releases and the upload entry point. */
export function AppDetail({ appId, onBack }: { appId: string; onBack: () => void }) {
  const [app, setApp] = useState<(DeveloperApp & { releases: ReleaseView[] }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api
      .app(appId)
      .then(setApp)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Ошибка'));
  }, [appId]);

  useEffect(load, [load]);

  if (error) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-danger">{error}</p>
      </Page>
    );
  }

  if (!app) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-muted">Загрузка…</p>
      </Page>
    );
  }

  return (
    <Page>
      <header className="flex items-center justify-between border-b border-border py-4">
        <div className="min-w-0">
          <button onClick={onBack} className="text-sm text-muted hover:text-fg">
            ← Мои приложения
          </button>
          <p className="mt-1 truncate font-semibold">{app.name}</p>
          <p className="truncate text-xs text-muted">{app.id}</p>
        </div>
        <Button size="sm" onClick={() => setUploading(true)}>
          Добавить версию
        </Button>
      </header>

      <main className="py-6">
        <h2 className="mb-3 text-lg font-semibold">Версии</h2>
        {app.releases.length === 0 ? (
          <EmptyState
            title="Версий пока нет"
            hint="Добавьте ссылку на установщик и его хеш — после проверки версия появится в каталоге."
          />
        ) : (
          <ul className="list-none space-y-2 p-0">
            {app.releases.map((release) => (
              <li
                key={release.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-surface p-4"
              >
                <div>
                  <p className="font-medium">Версия {release.version}</p>
                  <p className="text-xs text-muted">
                    {release.kind.toUpperCase()} · {release.scope} ·{' '}
                    {(release.sizeBytes / 1024 / 1024).toFixed(1)} МБ
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={releaseTone(release.status)}>{releaseLabel(release.status)}</Badge>
                  {release.status === 'in_review' && release.avStatus === 'pending' ? (
                    <span className="text-xs text-muted">проверка файла…</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {uploading ? (
        <AddReleaseDialog
          appId={app.id}
          onClose={() => setUploading(false)}
          onSubmitted={() => {
            setUploading(false);
            load();
          }}
        />
      ) : null}
    </Page>
  );
}

function releaseLabel(status: ReleaseView['status']): string {
  switch (status) {
    case 'published':
      return 'опубликована';
    case 'in_review':
      return 'на проверке';
    case 'rejected':
      return 'отклонена';
    case 'yanked':
      return 'отозвана';
  }
}

function releaseTone(status: ReleaseView['status']): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'published':
      return 'success';
    case 'in_review':
      return 'warning';
    case 'rejected':
    case 'yanked':
      return 'danger';
  }
}
